import React, { useState } from 'react';
import ExtensionInfoForm from './components/ExtensionInfoForm/ExtensionInfoForm';
import ExtensionSummary from './components/ExtensionSummary/ExtensionSummary';
import Navigation from './components/Navigation/Navigation';
import { createStyles } from './Conversion.styles';

import { STUDENTS_VIEW } from './components/Navigation/constants';
import {
  SpaceGroup,
  DestinationAssignmentData,
  AssignmentData,
} from './Conversion.types';
import AssignmentSelection from './components/AssignmentSelection/AssignmentSelection';
import ConversionDetails from './components/ConversionDetails/ConversionDetails';
import getStudentsByGroups from './getStudentsByGroups';
import getSuggestedTitleForAnExtension from './getSuggestedTitle';
import NavigationView from './NavigationView';
import filterDestinationAssignmentsForConversion from './filterDestinationAssignmentsForConversion';
import {
  Box,
  Divider,
  IconButton,
  Icons,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { LINK_ASSIGNMENT_TITLE, LINK_EXTENSION_TITLE_ID } from './constants';
import { DestinationAssignmentProps } from '../types';

type ConversionProps = {
  dialogTitle: string;
  spaceGroups: SpaceGroup[];
  data: DestinationAssignmentData;
  assignmentData: AssignmentData;
  assignments: DestinationAssignmentProps[];
  assignment: DestinationAssignmentProps;
  setData: (newProps: any) => void;
  setStep?: (step: string) => void;
  setAssignmentData: (newProps: any) => void;
  onClose: (callback?: () => void) => void;
  hasError?: boolean;
  greyedOutUsers: string[];
};

const Conversion = ({
  data: { parentAssignmentTitle, destinationAssignmentId },
  assignmentData: { title, assignedStudents, unassignedStudents, groups },
  assignments,
  assignment,
  setData,
  setAssignmentData,
  setStep,
  spaceGroups,
  onClose,
  hasError = false,
  greyedOutUsers,
}: ConversionProps) => {
  const navigationView = STUDENTS_VIEW;
  const groupsStudents = getStudentsByGroups(groups, spaceGroups);
  const selectedStudentsNumber =
    navigationView === STUDENTS_VIEW
      ? assignedStudents.length
      : groupsStudents.length;
  const [edited, setEdited] = useState<boolean>(false);
  const styles = createStyles(useTheme());
  const isDesktop = useMediaQuery(useTheme().breakpoints.up('md'));

  const updateDestinationAssignmentData = (id: string) => {
    const destination: any = assignments.find((item) => item.id === id);
    const parentTitle = destination.title;
    setData({
      parentAssignmentTitle: parentTitle,
      destinationAssignmentId: id,
    });
    setAssignmentData({
      title: !edited ? getSuggestedTitleForAnExtension(assignments, id) : title,
    });
  };

  if (!parentAssignmentTitle) {
    updateDestinationAssignmentData(
      filterDestinationAssignmentsForConversion(assignments, assignment)[0].key
    );
  }

  if (!title && !edited) {
    const suggestedName = getSuggestedTitleForAnExtension(assignments);
    setAssignmentData({ title: suggestedName });
  }

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.topContent}>
        <Box sx={styles.modalHeader}>
          <Typography
            id={LINK_EXTENSION_TITLE_ID}
            component="h2"
            sx={styles.modalTitle}
          >
            {LINK_ASSIGNMENT_TITLE}
          </Typography>

          <IconButton
            aria-label="Close dialog"
            onClick={() => onClose()}
            sx={styles.modalClose}
          >
            <Tooltip title={isDesktop ? 'Close' : ''}>
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>
        {/* Extension name info */}
        <ExtensionInfoForm
          isConversion
          hasTitle={false}
          extensionName={title}
          onExtensionNameChange={(newTitle: string) => {
            setAssignmentData({ title: newTitle });
            setEdited(true);
          }}
        />
        {/* Destination assignment name */}
        <AssignmentSelection
          assignments={filterDestinationAssignmentsForConversion(
            assignments,
            assignment
          )}
          value={
            destinationAssignmentId ||
            filterDestinationAssignmentsForConversion(
              assignments,
              assignment
            )[0].key
          }
          onChange={(id) => {
            updateDestinationAssignmentData(id);
          }}
        />
        {/* Conversion details */}
        <ConversionDetails
          currentTitle={assignment.title}
          convertedExtensionTitle={title}
          parentAssignmentTitle={parentAssignmentTitle}
        />
        <Divider sx={{ mt: '0.5rem', mb: '0.25rem' }} />
        {/* Extension summary */}
        <ExtensionSummary
          selectedStudentsNumber={selectedStudentsNumber}
          leftStudentsNumber={
            unassignedStudents.length - assignedStudents.length
          }
          navigationView={navigationView}
          setStep={setStep}
          isConversion
        />
        <Navigation />
      </Box>
      <Box sx={styles.navigationSection}>
        <NavigationView
          data={{ assignedStudents, unassignedStudents, groups }}
          setData={setAssignmentData}
          spaceGroups={spaceGroups}
          navigationView={navigationView}
          hasError={hasError}
          greyedOutUsers={greyedOutUsers}
        />
      </Box>
    </Box>
  );
};

export default React.memo(Conversion);
